import React, { useState } from "react";
import {
  Award,
  SearchIcon,
  Plus,
  Filter,
  RefreshCw,
  MoreVertical,
  Edit,
  Trash,
  Eye,
  Settings as SettingsIcon,
  Info,
} from "lucide-react";
import {
  TextField,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Box,
  Modal,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Tooltip,
} from "@mui/material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: "90vh",
  overflow: "auto",
};

const LoyaltyPoints = () => {
  // State for search and filter
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Modal states
  const [addPointsModal, setAddPointsModal] = useState(false);
  const [viewPointsModal, setViewPointsModal] = useState(false);
  const [editPointsModal, setEditPointsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Form states
  const [customerId, setCustomerId] = useState("");
  const [points, setPoints] = useState(0);
  const [reason, setReason] = useState("");

  // Mock data for customers with loyalty points
  const [customers, setCustomers] = useState([
    {
      id: 1,
      name: "John Doe",
      phone: "9876543210",
      points: 250,
      transactions: 12,
      lastUpdated: "2023-03-15",
    },
    {
      id: 2,
      name: "Jane Smith",
      phone: "8765432109",
      points: 180,
      transactions: 8,
      lastUpdated: "2023-03-10",
    },
    {
      id: 3,
      name: "Michael Johnson",
      phone: "7654321098",
      points: 350,
      transactions: 15,
      lastUpdated: "2023-03-20",
    },
    {
      id: 4,
      name: "Emily Brown",
      phone: "6543210987",
      points: 120,
      transactions: 5,
      lastUpdated: "2023-03-05",
    },
    {
      id: 5,
      name: "David Wilson",
      phone: "5432109876",
      points: 420,
      transactions: 18,
      lastUpdated: "2023-03-22",
    },
  ]);

  // Handler for opening and closing the context menu
  const handleMenuOpen = (event, customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handlers for modals
  const handleOpenAddModal = () => {
    setAddPointsModal(true);
  };

  const handleOpenViewModal = customer => {
    setSelectedCustomer(customer);
    setViewPointsModal(true);
    handleMenuClose();
  };

  const handleOpenEditModal = customer => {
    setSelectedCustomer(customer);
    setPoints(customer.points);
    setEditPointsModal(true);
    handleMenuClose();
  };

  // Form submission handlers
  const handleAddPoints = () => {
    // Add logic for adding points to a customer
    setAddPointsModal(false);
    // Reset form
    setCustomerId("");
    setPoints(0);
    setReason("");
  };

  const handleUpdatePoints = () => {
    // Update points for the selected customer
    const updatedCustomers = customers.map(c =>
      c.id === selectedCustomer.id ? { ...c, points: points } : c,
    );
    setCustomers(updatedCustomers);
    setEditPointsModal(false);
  };

  const handleDeletePoints = customer => {
    // Delete/reset loyalty points for a customer
    const updatedCustomers = customers.map(c =>
      c.id === customer.id ? { ...c, points: 0 } : c,
    );
    setCustomers(updatedCustomers);
    handleMenuClose();
  };

  // Filter customers based on search query
  const filteredCustomers = customers.filter(
    customer =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.phone.includes(searchQuery),
  );

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Header Section - Updated to match image 1 */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold font-outfit text-[#374151]">
            Loyalty Points Management
          </h1>
          <Tooltip
            title={
              <div style={{ padding: "5px 2px" }}>
                <p
                  style={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    fontSize: "15px",
                  }}>
                  Loyalty Points System
                </p>
                <p style={{ marginBottom: "8px" }}>
                  The Loyalty Points system rewards customers for their
                  purchases and interactions with your business.
                </p>
                <ul style={{ paddingLeft: "15px", listStyleType: "disc" }}>
                  <li>
                    Customers earn points through purchases and activities
                  </li>
                  <li>Points can be redeemed for discounts and rewards</li>
                  <li>Configure rewards through Redemption Rules</li>
                  <li>Increase customer retention and satisfaction</li>
                </ul>
              </div>
            }
            placement="bottom-start"
            arrow
            sx={{
              maxWidth: "400px",
              "& .MuiTooltip-tooltip": {
                fontSize: "14px",
                padding: "16px",
                lineHeight: "1.6",
                backgroundColor: "#374151",
                color: "white",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              },
              "& .MuiTooltip-arrow": {
                color: "#374151",
              },
            }}>
            <IconButton
              sx={{
                color: "#5652B0",
                width: "28px",
                height: "28px",
                padding: "4px",
                marginLeft: "2px",
                "&:hover": { backgroundColor: "rgba(86, 82, 176, 0.08)" },
              }}>
              <Info size={16} />
            </IconButton>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            sx={{
              color: "#5652B0",
              border: "1px solid #E5E7EB",
              borderRadius: "4px",
              p: "8px",
            }}>
            <RefreshCw size={18} />
          </IconButton>
          <IconButton
            sx={{
              color: "#5652B0",
              border: "1px solid #E5E7EB",
              borderRadius: "4px",
              p: "8px",
            }}>
            <SettingsIcon size={18} />
          </IconButton>
        </div>
      </div>

      {/* Search and Filter Bar - Updated to match image 1 */}
      <div className="flex justify-between items-center py-4 px-6 border-b border-[#E5E7EB] bg-white">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex items-center w-full max-w-md">
            <SearchIcon className="absolute left-3 text-gray-400" size={20} />
            <TextField
              placeholder="Search categories by name..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  paddingLeft: "40px",
                  borderRadius: "8px",
                  backgroundColor: "#F9FAFB",
                },
              }}
            />
          </div>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleOpenAddModal}
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4B47A1" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}>
            Add Points
          </Button>
        </div>
      </div>

      {/* Filter Controls - Similar to image 1 */}
      <div className="flex items-center gap-4 py-4 px-6 border-b border-[#E5E7EB]">
        <div className="flex flex-col">
          {/* <span className="text-sm text-gray-500 mb-1">Status</span> */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select value="all" displayEmpty sx={{ borderRadius: "8px" }}>
              <MenuItem value="all">All Customers</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="flex flex-col">
          {/* <span className="text-sm text-gray-500 mb-1">Points Range</span> */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select value="all" displayEmpty sx={{ borderRadius: "8px" }}>
              <MenuItem value="all">All Points</MenuItem>
              <MenuItem value="high">High (&gt;300)</MenuItem>
              <MenuItem value="medium">Medium (100-300)</MenuItem>
              <MenuItem value="low">Low (&lt;100)</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="flex flex-col">
          {/* <span className="text-sm text-gray-500 mb-1">Sort By</span> */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select value="name" displayEmpty sx={{ borderRadius: "8px" }}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="points">Points (High to Low)</MenuItem>
              <MenuItem value="date">Last Updated</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Customers List */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#F9FAFB]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Points
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transactions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCustomers.map(customer => (
                <tr key={customer.id} className="hover:bg-[#F9FAFB]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      #{customer.id}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {customer.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {customer.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      <Award size={14} className="mr-1" />
                      {customer.points} points
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {customer.transactions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {customer.lastUpdated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <IconButton
                      aria-label="view"
                      onClick={() => handleOpenViewModal(customer)}
                      sx={{ color: "#5652B0" }}>
                      <Eye size={18} />
                    </IconButton>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpenEditModal(customer)}
                      sx={{ color: "#5652B0" }}>
                      <Edit size={18} />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDeletePoints(customer)}
                      sx={{ color: "#EF4444" }}>
                      <Trash size={18} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredCustomers.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-gray-500">
                No customers found with the given search criteria
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add Points Modal */}
      <Modal
        open={addPointsModal}
        onClose={() => setAddPointsModal(false)}
        aria-labelledby="add-points-modal">
        <Box sx={modalStyle}>
          <div className="border-b border-[#E5E7EB] px-6 py-4">
            <h2 className="text-lg font-semibold text-[#374151]">
              Add Loyalty Points
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <FormControl fullWidth>
                <InputLabel id="customer-select-label">
                  Select Customer
                </InputLabel>
                <Select
                  labelId="customer-select-label"
                  id="customer-select"
                  value={customerId}
                  label="Select Customer"
                  onChange={e => setCustomerId(e.target.value)}>
                  {customers.map(cust => (
                    <MenuItem key={cust.id} value={cust.id}>
                      {cust.name} ({cust.phone})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Points to Add"
                type="number"
                value={points}
                onChange={e => setPoints(e.target.value)}
                InputProps={{ inputProps: { min: 1 } }}
              />

              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={2}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g., Purchase, Referral, Special Promotion"
              />
            </div>
          </div>
          <div className="bg-[#F9FAFB] px-6 py-4 flex justify-end space-x-2">
            <Button
              variant="outlined"
              onClick={() => setAddPointsModal(false)}
              sx={{ borderRadius: "8px", textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddPoints}
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": { backgroundColor: "#4B47A1" },
                borderRadius: "8px",
                textTransform: "none",
              }}>
              Add Points
            </Button>
          </div>
        </Box>
      </Modal>

      {/* View Points Modal */}
      <Modal
        open={viewPointsModal}
        onClose={() => setViewPointsModal(false)}
        aria-labelledby="view-points-modal">
        <Box sx={modalStyle}>
          <div className="border-b border-[#E5E7EB] px-6 py-4">
            <h2 className="text-lg font-semibold text-[#374151]">
              Loyalty Points Details
            </h2>
          </div>
          {selectedCustomer && (
            <div className="p-6">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium">
                    {selectedCustomer.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedCustomer.phone}
                  </p>
                </div>
                <div className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full flex items-center">
                  <Award size={18} className="mr-2" />
                  <span className="font-semibold">
                    {selectedCustomer.points} points
                  </span>
                </div>
              </div>

              <div className="border border-[#E5E7EB] rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-2">Points Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Transactions</p>
                    <p className="font-medium">
                      {selectedCustomer.transactions}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="font-medium">
                      {selectedCustomer.lastUpdated}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Points Value</p>
                    <p className="font-medium">
                      ₹{(selectedCustomer.points * 0.25).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <Chip
                      label={
                        selectedCustomer.points > 200
                          ? "Gold Member"
                          : "Regular Member"
                      }
                      size="small"
                      sx={{
                        backgroundColor:
                          selectedCustomer.points > 200 ? "#FEF3C7" : "#E0E7FF",
                        color:
                          selectedCustomer.points > 200 ? "#92400E" : "#4338CA",
                      }}
                    />
                  </div>
                </div>
              </div>

              <h4 className="font-medium mb-2">Recent Point Activities</h4>
              <div className="border border-[#E5E7EB] rounded-lg divide-y">
                <div className="p-3 flex justify-between">
                  <div>
                    <p className="font-medium">Purchase #1234</p>
                    <p className="text-sm text-gray-500">2023-03-15</p>
                  </div>
                  <div className="text-green-600 font-medium">+25 points</div>
                </div>
                <div className="p-3 flex justify-between">
                  <div>
                    <p className="font-medium">Referral Bonus</p>
                    <p className="text-sm text-gray-500">2023-03-10</p>
                  </div>
                  <div className="text-green-600 font-medium">+50 points</div>
                </div>
                <div className="p-3 flex justify-between">
                  <div>
                    <p className="font-medium">Point Redemption</p>
                    <p className="text-sm text-gray-500">2023-03-05</p>
                  </div>
                  <div className="text-red-600 font-medium">-100 points</div>
                </div>
              </div>
            </div>
          )}
          <div className="bg-[#F9FAFB] px-6 py-4 flex justify-end">
            <Button
              variant="outlined"
              onClick={() => setViewPointsModal(false)}
              sx={{ borderRadius: "8px", textTransform: "none" }}>
              Close
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Edit Points Modal */}
      <Modal
        open={editPointsModal}
        onClose={() => setEditPointsModal(false)}
        aria-labelledby="edit-points-modal">
        <Box sx={modalStyle}>
          <div className="border-b border-[#E5E7EB] px-6 py-4">
            <h2 className="text-lg font-semibold text-[#374151]">
              Edit Loyalty Points
            </h2>
          </div>
          {selectedCustomer && (
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-1">Customer</p>
                <p className="font-medium">
                  {selectedCustomer.name} ({selectedCustomer.phone})
                </p>
              </div>

              <TextField
                fullWidth
                label="Current Points"
                type="number"
                value={points}
                onChange={e => setPoints(parseInt(e.target.value))}
                InputProps={{ inputProps: { min: 0 } }}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Reason for Update"
                multiline
                rows={2}
                placeholder="Provide a reason for updating points"
              />
            </div>
          )}
          <div className="bg-[#F9FAFB] px-6 py-4 flex justify-end space-x-2">
            <Button
              variant="outlined"
              onClick={() => setEditPointsModal(false)}
              sx={{ borderRadius: "8px", textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdatePoints}
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": { backgroundColor: "#4B47A1" },
                borderRadius: "8px",
                textTransform: "none",
              }}>
              Update Points
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default LoyaltyPoints;
